import { LocalDateTime } from 'js-joda';
import { stringify } from '@tock/query-string';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import clsx from 'clsx';

import { ActionsType, StoreType } from '@tock/shared/utils/connect';
import { Button, TITLE_TEXT, TockDialog, Typography } from 'omakase';
import { PurchaseDetails } from 'components/business/receipts/PurchaseDetails';
import { consumer, core } from '@tock/coretypes';
import { isLockOfEvent, removeLocks } from 'utils/lock';
import { sessionTokenSelector } from 'selectors/appSelector';
import { useMountEffect } from '@tock/hooks';
import LoadingComponent from '@tock/shared/components/LoadingComponent';

interface Props {
  app: StoreType['app'];
  business: consumer.IBusinessConfig;
  explorePath: string;
  lock: StoreType['lock'];
  queryParam: StoreType['queryParam'];
  lockActions: ActionsType['lock'];
  selectionActions: ActionsType['selection'];
  setConfirmSelectionDialogVisible(arg: boolean): void;
  showConfirmSelectionDialog: boolean;
}

const ConfirmSelection: React.FunctionComponent<Props> = ({
  setConfirmSelectionDialogVisible,
  lock: { currentLock },
  business,
  showConfirmSelectionDialog,
  lockActions,
  selectionActions,
  queryParam,
  explorePath,
}) => {
  const sessionToken = useSelector(sessionTokenSelector);

  useMountEffect(() => {
    setConfirmSelectionDialogVisible(false);
  });

  const handleClose = () => {
    setConfirmSelectionDialogVisible(false);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    removeLocks(lockActions, selectionActions, business.id!);
  };

  const getQuery = () => {
    const query: Dictionary<string> = {
      ...queryParam.query,
    };
    if (!query['tock_source']) {
      query['tock_source'] = 'tock_widget';
    }

    // If running in a widget
    if (window.parent !== window && sessionToken) {
      query.sessionToken = sessionToken;
    }

    return query;
  };

  const getConsumerUrl = () => {
    if (window.location.hostname === 'localhost') {
      return `${window.location.origin}/${business.domainName}`;
    }

    //should only be rendered in widget
    return `https://${explorePath}/${business.domainName}`;
  };

  const handleConfirmSelection = () => {
    const goBackTo = window.location.origin;
    const consumerUrl = getConsumerUrl();
    const query = getQuery();

    // If running in a widget
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          source: 'tock',
          action: 'redirect',
          redirectTo: `${consumerUrl}/checkout/edit-guest-profile?${stringify(query)}`,
        },
        '*'
      );
    } else {
      query.goBackTo = goBackTo;
      // When having a reservation lock, do a full page refresh to load checkout flow.
      // eslint-disable-next-line xss/no-location-href-assign
      window.location.href = `${consumerUrl}/checkout/edit-guest-profile?${stringify(query)}`;
    }
  };

  if (!showConfirmSelectionDialog || !currentLock) {
    return null;
  }
  const ticketSubset: core.ITicketSubset = _.first(currentLock.ticketSubset) || {};
  const dateTime = LocalDateTime.parse(ticketSubset.requestedDateTime || currentLock.dateTime);
  const ticketTypeName = currentLock.ticketType[0].name || '';
  const contentClassName = clsx('ConfirmationModal-reservationContent');

  const partySize = ticketSubset.ticketCount;

  const footerButton = (
    <Button
      data-testid="confirm-selection"
      size="medium"
      onClick={handleConfirmSelection}
      fullWidth
      variant="primary"
    >
      Continue
    </Button>
  );

  return (
    <TockDialog
      headingConfig={{
        left: {
          configType: 'title',
          title: 'Review your selection',
        },
        right: {
          configType: 'closeButton',
        },
      }}
      open={showConfirmSelectionDialog}
      onClose={handleClose}
      aria-label={TITLE_TEXT}
      type="partial"
      actionsContent={footerButton}
    >
      <LoadingComponent showOverlay={true} isLoading={false} isBallPulse={true}>
        <div className={contentClassName}>
          <div className="ConfirmationModal-reservationContentBody">
            <div className="ConfirmationModal-reservationContentParagraph">
              <Typography color="inherit" component="span" variant="body02">
                Excellent choice! Review your selection below and continue to complete your
                reservation.
              </Typography>
            </div>
            <PurchaseDetails
              business={business}
              dateTime={dateTime}
              isEvent={isLockOfEvent(currentLock)}
              diner={undefined}
              owner={undefined}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              partySize={partySize!}
              ticketTypeName={ticketTypeName}
              ticketType={_.first(currentLock.ticketType) as core.TicketType}
              seatingAreaName={ticketSubset.seatingArea?.name || undefined}
            />
          </div>
        </div>
      </LoadingComponent>
    </TockDialog>
  );
};

export default ConfirmSelection;
